import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { api } from "../../shared/api/client";
import { BRAND_NAME } from "../../shared/brand";
import { Button } from "../../shared/ui/Button";
import { EmptyState } from "../../shared/ui/EmptyState";
import { LoadingBlock } from "../../shared/ui/LoadingBlock";

type PublicReport = {
  reportPeriod: string;
  expiresAt: string;
  snapshot: {
    student: { name: string };
    positivePerformances: { title: string; content: string; happenedAt?: string; tags?: string[] }[];
    goalProgress: { title: string; status: string; latestResult?: string }[];
    attendance: Record<string, number>;
    teacherComment: string;
    nextWeekGoal: string;
  };
};

export function ParentReportPage() {
  const { token = "" } = useParams();
  const [feedback, setFeedback] = useState("");
  const [sent, setSent] = useState(false);
  const report = useQuery({
    queryKey: ["public-parent-report", token],
    queryFn: async () => (await api<PublicReport>(`/api/public/parent-reports/${token}`)).data!
  });
  const acknowledge = useMutation({
    mutationFn: () => api(`/api/public/parent-reports/${token}/acknowledge`, { method: "POST", body: "{}" })
  });
  const submit = useMutation({
    mutationFn: () => api(`/api/public/parent-reports/${token}/feedback`, { method: "POST", body: JSON.stringify({ content: feedback }) }),
    onSuccess: () => { setFeedback(""); setSent(true); }
  });

  if (report.isLoading) return <div className="parent-h5"><LoadingBlock /></div>;
  if (report.error || !report.data) return <div className="parent-h5"><EmptyState title="周报无法打开" detail={report.error instanceof Error ? report.error.message : "链接可能已过期或被撤回。"} /></div>;
  const data = report.data;
  const attendanceLabels: Record<string, string> = { present: "出勤", late: "迟到", leave: "请假", absent: "缺勤" };
  return (
    <main className="parent-h5">
      <header className="parent-h5-head"><span>{BRAND_NAME}</span><h1>{data.snapshot.student.name}的成长周报</h1><p>{data.reportPeriod}</p></header>
      <section><h2>本周成长</h2>{data.snapshot.positivePerformances.length ? data.snapshot.positivePerformances.map((item, index) => <article className="parent-growth-item" key={`${item.title}-${index}`}><strong>{item.title}</strong><p>{item.content}</p>{item.tags?.length ? <div>{item.tags.map((tag) => <span className="chip" key={tag}>{tag}</span>)}</div> : null}</article>) : <p className="parent-empty">本周暂未记录成长证据。</p>}</section>
      <section><h2>目标进展</h2>{data.snapshot.goalProgress.length ? data.snapshot.goalProgress.map((goal, index) => <div className="parent-goal" key={`${goal.title}-${index}`}><strong>{goal.title}</strong><span>{goal.latestResult || goal.status}</span></div>) : <p className="parent-empty">本周暂无个人目标更新。</p>}</section>
      <section><h2>考勤摘要</h2><div className="parent-attendance">{Object.entries(data.snapshot.attendance).map(([status, count]) => <div key={status}><strong>{count}</strong><span>{attendanceLabels[status] || status}</span></div>)}</div></section>
      <section><h2>老师寄语</h2><p>{data.snapshot.teacherComment || "继续保持，期待下一周的成长。"}</p></section>
      <section><h2>下周目标</h2><p>{data.snapshot.nextWeekGoal || "稳步前进，完成好每天的小目标。"}</p></section>
      <section className="parent-feedback"><h2>家长反馈</h2>{sent ? <p className="parent-success">反馈已提交，老师会在系统中查看。</p> : <><textarea rows={4} maxLength={500} value={feedback} placeholder="最多 500 字" onChange={(event) => setFeedback(event.target.value)} /><span>{feedback.length}/500</span>{submit.error ? <p className="form-error">{submit.error instanceof Error ? submit.error.message : "反馈提交失败"}</p> : null}<Button type="button" disabled={!feedback.trim() || submit.isPending} onClick={() => submit.mutate()}>{submit.isPending ? "提交中…" : "提交反馈"}</Button></>}</section>
      <footer><Button variant="secondary" type="button" disabled={acknowledge.isSuccess || acknowledge.isPending} onClick={() => acknowledge.mutate()}>{acknowledge.isSuccess ? "已确认阅读" : "确认已阅读"}</Button>{acknowledge.error ? <span className="form-error">{acknowledge.error instanceof Error ? acknowledge.error.message : "确认失败"}</span> : null}<small>本页面仅展示当前学生的已发布快照 · 到期 {new Date(data.expiresAt).toLocaleDateString("zh-CN")}</small></footer>
    </main>
  );
}
